import { useEffect, useState } from "react";

import { Button } from "@/common/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/common/ui/dialog";
import { Input } from "@/common/ui/input";
import { Label } from "@/common/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/common/ui/select";

import { createOrgUnit } from "../api/organization-api";
import { ORG_UNIT_TYPE_OPTIONS } from "../org-unit-types";
import { slugFromName } from "../slug";
import type { OrgUnitType } from "../types";

/** identity's org-unit slug: a name's slug without a leading or trailing hyphen. */
function orgUnitSlug(name: string): string {
  return slugFromName(name).replace(/^-+|-+$/g, "");
}

interface AddOrgUnitModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  enterpriseId: string;
  parentId: string;
  parentName: string;
  onUnitCreated: () => void;
}

export function AddOrgUnitModal({
  open,
  onOpenChange,
  enterpriseId,
  parentId,
  parentName,
  onUnitCreated,
}: AddOrgUnitModalProps): React.ReactElement {
  const [name, setName] = useState("");
  const [type, setType] = useState<OrgUnitType>("team");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setName("");
      setType("team");
      setError("");
    }
  }, [open]);

  const handleSubmit = async (): Promise<void> => {
    setError("");

    if (!name.trim()) {
      setError("Name is required");
      return;
    }

    setIsSubmitting(true);

    try {
      await createOrgUnit({
        enterprise_id: enterpriseId,
        parent_id: parentId,
        name: name.trim(),
        slug: orgUnitSlug(name.trim()),
        org_unit_type: type,
      });
      onUnitCreated();
      onOpenChange(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create unit");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = (): void => {
    onOpenChange(false);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) onOpenChange(false);
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add Organization Unit</DialogTitle>
          <DialogDescription>
            Create a new unit under <strong>{parentName}</strong>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="unit-name">Name</Label>
            <Input
              id="unit-name"
              placeholder="e.g., Engineering Team"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                setError("");
              }}
              className={error ? "border-destructive" : ""}
            />
            {error && <p className="text-sm text-destructive">{error}</p>}
          </div>

          <div className="space-y-2">
            <Label>Type</Label>
            <Select value={type} onValueChange={(value) => setType(value as OrgUnitType)}>
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ORG_UNIT_TYPE_OPTIONS.map((item) => {
                  const Icon = item.icon;
                  return (
                    <SelectItem key={item.value} value={item.value}>
                      <div className="flex items-center gap-2">
                        <Icon className="h-4 w-4" />
                        {item.label}
                      </div>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? "Creating..." : "Create Unit"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
